/**
 * useEmployeeFilters — derived filtered + sorted Employee-Liste.
 *
 * Erwartet:
 *   employees:  alle Mitarbeiter
 *   massnahmen: alle Massnahmen (fuer Massnahmen-Filter via Schicht-Join)
 *   shifts:     Schichten (date >= heute - 30d), zur Mitarbeiter→Massnahmen-Map
 *   filters:    aktueller Filter-Zustand (siehe FilterPanel.jsx)
 *   sortKey:    'last_name' | 'first_name' | 'city' | 'birthday' | 'employment_start'
 *
 * Liefert: filtered (Liste), counts (pro Filter wieviele aktiv).
 */
import { useMemo } from 'react';
import { normalizeFirstLetter } from './AZFilterStrip';
import { fuzzyMatch } from '../../lib/searchUtils';
import { orgUnitSubtreeSets, employeeMatchesOrgUnit } from '../../components/OrgUnitFilter';

const norm = (s) => (s || '').toString().trim().toLowerCase();

const monthOf = (iso) => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? parseInt(m[2], 10) : null;
};

const daysUntilNextBirthday = (iso) => {
  if (!iso) return null;
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const today = new Date();
  const next = new Date(today.getFullYear(), parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  if (next < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
    next.setFullYear(today.getFullYear() + 1);
  }
  return Math.round((next - today) / 86400000);
};

export const buildEmployeeMassnahmenMap = (shifts) => {
  const map = new Map();
  for (const s of shifts || []) {
    if (!s.employee_id || !s.massnahme_id) continue;
    if (!map.has(s.employee_id)) map.set(s.employee_id, new Set());
    map.get(s.employee_id).add(s.massnahme_id);
  }
  return map;
};

// Qualifikations-Namen (unterstützt Alt-Format String + Objekt {name}).
export const empQualNames = (emp) =>
  (emp?.qualifications || [])
    .map((q) => (typeof q === 'object' ? q?.name : q))
    .filter(Boolean);

// Weiterbildungs-/Schulungs-Titel.
export const empTrainingTitles = (emp) =>
  (emp?.trainings || []).map((t) => t?.title).filter(Boolean);

export const useEmployeeFilters = ({ employees, filters, empMassnahmenMap, sortKey = 'last_name', sortDir = 'asc', orgTree = [] }) => {
  return useMemo(() => {
    const f = filters || {};
    const search = (f.search || '').toString().trim();
    const orgSets = orgUnitSubtreeSets(orgTree, f.org_unit);

    const list = (employees || []).filter((emp) => {
      // Suche — Umlaut-Normalisiert + Tippfehler-Toleranz (Levenshtein <= 2)
      if (search) {
        const haystack = [
          emp.first_name, emp.last_name, emp.employee_number,
          emp.business_email, emp.email, emp.position, emp.department,
          ...(emp.position_list || []),
          emp.city, emp.postal_code,
          ...empQualNames(emp),
          ...empTrainingTitles(emp),
        ].filter(Boolean).join(' ');
        if (!fuzzyMatch(haystack, search)) return false;
      }

      // Organisationseinheit (Baum-Teilbaum: Standort/Niederlassung/Abteilung)
      if (!employeeMatchesOrgUnit(emp, orgSets)) return false;

      // Status
      if (f.status === 'active' && !emp.is_active) return false;
      if (f.status === 'inactive' && emp.is_active) return false;

      // Personalstatus: Anwärter vs. Mitarbeiter
      if (f.onboarding === 'anwaerter' && emp.onboarding_status !== 'anwaerter') return false;
      if (f.onboarding === 'mitarbeiter' && emp.onboarding_status === 'anwaerter') return false;

      // A-Z-Schnellfilter (Anfangsbuchstabe Nachname; '#' = sonstige Zeichen)
      if (f.az_letter) {
        if (normalizeFirstLetter(emp.last_name) !== f.az_letter) return false;
      }

      // Befristet
      if (f.temporary === 'yes' && !emp.is_temporary) return false;
      if (f.temporary === 'no' && emp.is_temporary) return false;

      // Multi-select-Felder
      // departments enthält die Freitext-Werte aus emp.department.
      // Wir splitten den Eintrag des Mitarbeiters an Komma/Semikolon, damit
      // ein Mitarbeiter mit "BaE, BvB" auch beim Filter "BvB" matcht.
      if (f.departments?.length) {
        const empTokens = (emp.department || '')
          .split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        const matchSet = new Set([...(empTokens.length ? empTokens : [emp.department || ''])]);
        if (!f.departments.some((d) => matchSet.has(d))) return false;
      }
      if (f.positions?.length) {
        // Filter matched, wenn Hauptfunktion ODER eine Zweit-/Drittfunktion
        // in der gewählten Liste vorkommt
        const empPositions = new Set([
          ...(emp.position ? [emp.position] : []),
          ...((emp.position_list || []).filter(Boolean)),
        ]);
        if (!f.positions.some((p) => empPositions.has(p))) return false;
      }
      if (f.employment_types?.length && !f.employment_types.includes(emp.employment_type || '')) return false;

      // Tätigkeitsart (Array; enthält 'operativ'/'verwaltung'; leer = operativ-Default)
      if (f.taetigkeitsart && f.taetigkeitsart !== 'all') {
        const raw = emp.taetigkeitsart;
        const arts = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        const has = f.taetigkeitsart === 'verwaltung'
          ? arts.includes('verwaltung')
          : (arts.includes('operativ') || arts.length === 0);
        if (!has) return false;
      }

      // Wohnort (Substring, case-insensitive)
      if (f.city && !norm(emp.city).includes(norm(f.city))) return false;

      // Geburtsmonat (multi-select 1-12)
      if (f.birth_months?.length) {
        const m = monthOf(emp.date_of_birth);
        if (!m || !f.birth_months.includes(m)) return false;
      }

      // Geburtstag-Quickfilter
      if (f.birthday_window) {
        const days = daysUntilNextBirthday(emp.date_of_birth);
        if (days === null) return false;
        if (f.birthday_window === 'week' && days > 7) return false;
        if (f.birthday_window === 'month' && days > 31) return false;
        if (f.birthday_window === '90d' && days > 90) return false;
      }

      // Maßnahmen (Mitarbeiter ist via Schicht zugeordnet)
      if (f.massnahmen?.length) {
        const set = empMassnahmenMap?.get(emp.id);
        if (!set || !f.massnahmen.some((mid) => set.has(mid))) return false;
      }

      // Qualifikationen (Mehrfachauswahl, Name-Match)
      if (f.qualifications?.length) {
        const names = new Set(empQualNames(emp));
        if (!f.qualifications.some((q) => names.has(q))) return false;
      }

      // Weiterbildungen (Mehrfachauswahl, Titel-Match)
      if (f.trainings?.length) {
        const titles = new Set(empTrainingTitles(emp));
        if (!f.trainings.some((t) => titles.has(t))) return false;
      }

      return true;
    });

    // Sortierung
    const cmp = (a, b) => {
      const dir = sortDir === 'desc' ? -1 : 1;
      const get = (e) => {
        switch (sortKey) {
          case 'first_name': return norm(e.first_name);
          case 'position': return norm(e.position);
          case 'city': return norm(e.city);
          case 'birthday':
            return daysUntilNextBirthday(e.date_of_birth) ?? 9999;
          case 'employment_start':
            return e.employment_start || '';
          case 'last_name':
          default:
            return norm(e.last_name) + ' ' + norm(e.first_name);
        }
      };
      const va = get(a); const vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    };
    list.sort(cmp);

    return list;
  }, [employees, filters, empMassnahmenMap, sortKey, sortDir, orgTree]);
};

export default useEmployeeFilters;
